import { forwardRef, useState, useLayoutEffect, useRef } from "react";
import { useForwardedOrLocalRef, useUniqueIds } from "../../../shared/hooks";
import classes from "./DDSTextarea.module.css";
import { clsx } from "clsx";
import { logMessage, getSite } from "@classdojo/log-client";

export const Sizes = ["small", "medium", "large"] as const;

type HTMLProps = React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;
type EventHandlers = Omit<
  {
    [K in keyof HTMLProps as K extends `on${string}` ? K : never]: HTMLProps[K];
  },
  "onChange"
>;
type OtherForwardedProps = Pick<
  HTMLProps,
  "id" | "name" | "value" | "disabled" | "defaultValue" | "required" | "autoFocus"
>;

export interface Props extends EventHandlers, OtherForwardedProps {
  /** The name will get used for automated product events */
  ["data-name"]: string;

  /** The label text shown above the textarea */
  label: string | React.ReactNode;

  /** The size of he label shown above the textarea */
  labelSize?: (typeof Sizes)[number];

  /**
   * Whether the label should be visually hidden. Refrain from using unless absolutely necessary —
   * a sighted user without a screen reader needs a visible label just as much as anyone else.
   *
   * The label is clipped out of view rather than hidden, so it still names the textarea. See
   * ai/rules/aria-labels.md.
   */
  visuallyHiddenLabel?: boolean;

  /** Placeholder text shown when textarea is empty */
  placeholder?: string;

  /** Whether the textarea shows error styling */
  error?: boolean;

  /** Helper text shown below the textarea */
  hint?: string | React.ReactNode;

  /** Number of visible text rows */
  rows?: number;

  /** Maximum number of characters allowed */
  maxLength?: number;

  /** Callback fired when the value changes */
  onChange?: (value: string, rawEvent: React.ChangeEvent<HTMLTextAreaElement>) => void;

  /** The value of the textarea */
  value?: string | number | readonly string[];

  /** Whether the textarea should be resizable. Defaults to true */
  enableResize?: boolean;

  /** Whether the textarea should have a border. Defaults to true */
  noBorder?: boolean;

  /** Children to render inside the textarea */
  children?: React.ReactNode;

  /** Maximum height for the textarea */
  maxHeight?: number;

  /** Icon to display on the right side of the textarea. Cannot be used together with leftIcon. */
  rightIcon?: React.ReactNode;

  /** Icon to display on the left side of the textarea. Cannot be used together with rightIcon. */
  leftIcon?: React.ReactNode;

  /**
   * IDs of elements that describe the textarea (space-separated, per HTML).
   * When `hint` is set, this is merged with the hint element's id so both the hint and any external
   * descriptions are associated with the textarea
   */
  "aria-describedby"?: string;
}

/**
 * Docs:
 * - {@link https://www.figma.com/design/erhFTAWNMugPVrJGCAWsse/%E2%9D%96-DDS---Product-Design-System--PDS-?node-id=22166-6065&t=fld7bTNyM4QTtZhu-0 Figma}
 * - {@link https://components.internal.classdojo.com/?path=/story/components-ddstextarea--basic Storybook}
 *
 * The DDSTextarea component provides a customizable textarea input with support for:
 * - Labels with different sizes
 * - Error states
 * - Hint text
 * - Auto-resizing
 * - Custom icon (either left or right position, not both)
 */

export const DDSTextarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      ["data-name"]: dataName,
      disabled,
      id,
      label,
      labelSize = "medium",
      visuallyHiddenLabel,
      error,
      hint,
      placeholder = " ",
      rows = 3,
      maxLength,
      onChange,
      value,
      enableResize = true,
      noBorder = false,
      children,
      maxHeight = 100,
      rightIcon,
      leftIcon,
      "aria-describedby": ariaDescribedByFromUser,
      ...rest
    },
    forwardedRef,
    // eslint-disable-next-line complexity
  ) => {
    const ref = useForwardedOrLocalRef(forwardedRef);
    const sizerRef = useRef<HTMLTextAreaElement>(null);
    const [labelId, textareaId, hintId] = useUniqueIds(["DDSTextarea.label", "DDSTextarea.input", "DDSTextarea.hint"]);
    const ariaDescribedByMerged =
      [hint ? hintId : undefined, ariaDescribedByFromUser]
        .filter((describedById): describedById is string => !!describedById?.trim())
        .join(" ") || undefined;

    const [height, setHeight] = useState<number | null>(null);
    const userHasManuallyResized = useRef(false);
    const heightOnMouseDownRef = useRef<number | null>(null);

    // Ensure only one icon is used
    if (leftIcon && rightIcon) {
      logMessage("WARN", `${getSite()}.textarea.bothIconsProvided`);
    }

    // Use only the right icon if both are provided (with warning in dev)
    const finalLeftIcon = leftIcon && !rightIcon ? leftIcon : null;
    const finalRightIcon = rightIcon || null;

    useLayoutEffect(() => {
      // Once the user manually resizes, we disable auto-resizing completely.
      // Note: auto-resize works even when enableResize is false (no resize handle),
      // so the textarea still grows with content.
      if (userHasManuallyResized.current || !sizerRef.current) {
        return;
      }

      const sizerHeight = Math.min(sizerRef.current.scrollHeight, maxHeight);
      setHeight((currentHeight) => {
        if (sizerHeight !== currentHeight) {
          return sizerHeight;
        }
        return currentHeight;
      });
    }, [value, maxHeight]);

    return (
      <div className={clsx(classes.textarea, error && classes.error)}>
        <label
          id={labelId}
          htmlFor={id ?? textareaId}
          className={clsx(visuallyHiddenLabel ? classes.visuallyHiddenLabel : classes.label, classes[labelSize])}
        >
          {label}
        </label>
        <div className={clsx(classes.inputBox, classes[labelSize], noBorder && classes.noBorder)}>
          <textarea
            className={clsx(
              classes.input,
              enableResize === false && classes.noResize,
              (children || finalRightIcon) && classes.extraPaddingRight,
              finalLeftIcon && classes.extraPaddingLeft,
            )}
            data-name={dataName}
            disabled={disabled}
            id={id ?? textareaId}
            placeholder={placeholder}
            ref={ref}
            aria-describedby={ariaDescribedByMerged}
            aria-invalid={error}
            rows={rows}
            maxLength={maxLength}
            onChange={(e) => {
              onChange?.(e.target.value, e);
            }}
            onMouseDown={() => {
              if (!enableResize || !ref.current) return;
              heightOnMouseDownRef.current = ref.current.offsetHeight;
            }}
            onMouseUp={() => {
              if (!enableResize || !ref.current || heightOnMouseDownRef.current === null) return;
              if (ref.current.offsetHeight !== heightOnMouseDownRef.current) {
                userHasManuallyResized.current = true;
                setHeight(ref.current.offsetHeight);
              }
              heightOnMouseDownRef.current = null;
            }}
            onTouchStart={() => {
              if (!enableResize || !ref.current) return;
              heightOnMouseDownRef.current = ref.current.offsetHeight;
            }}
            onTouchEnd={() => {
              if (!enableResize || !ref.current || heightOnMouseDownRef.current === null) return;
              if (ref.current.offsetHeight !== heightOnMouseDownRef.current) {
                userHasManuallyResized.current = true;
                setHeight(ref.current.offsetHeight);
              }
              heightOnMouseDownRef.current = null;
            }}
            value={value}
            style={{ height: height ? `${height}px` : "auto" }}
            {...rest}
          />
          <textarea
            // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- Hidden sizer textarea for auto-height calculation
            role="presentation"
            ref={sizerRef}
            aria-hidden="true"
            className={clsx(
              classes.input,
              classes.sizer,
              (children || finalRightIcon) && classes.extraPaddingRight,
              finalLeftIcon && classes.extraPaddingLeft,
            )}
            readOnly
            tabIndex={-1}
            value={value}
            rows={rows}
            placeholder={placeholder}
          />
          {finalLeftIcon && <div className={classes.leftIcon}>{finalLeftIcon}</div>}
          {children && <div className={classes.extraPaddingRight}>{children}</div>}
          {finalRightIcon && <div className={classes.rightIcon}>{finalRightIcon}</div>}
        </div>
        {hint && (
          <span
            role={error ? "alert" : undefined}
            aria-live={!error ? "polite" : undefined}
            id={hintId}
            className={classes.hint}
          >
            {hint}
          </span>
        )}
      </div>
    );
  },
);
